import {useEffect, useState} from 'react';
import {useFlags} from 'gatsby-plugin-launchdarkly';
import {useLaunchDarkly} from './useLaunchDarkly';
import {useTimeout} from './useTimeout';

/*
 * Types.
 */

interface UseFlagOptions {
  /** Default value to use in case of timeout or while loading. */
  defaultValue: boolean;
  /** Length of timeout in milliseconds. */
  timeout: number;
}

/**
 * Returns value of a LaunchDarkly flag plus LaunchDarkly's ready state, or the
 * default value if the flag doesn't exist or if LaunchDarkly is loading or
 * times out.
 *
 * Passing the ready state from useLaunchDarkly makes it more convenient for
 * consuming components to hide UI if they are waiting for an initial flag
 * value.
 *
 * The 800 ms default comes from going directly to the signup page's URL in an
 * incognito window and seeing how long the cold start takes to show up in ideal
 * conditions on a development workstation in the worst case, then doubling it.
 * Most page loads should not time out in ideal conditions, though in the wild
 * is a different story.
 *
 * Individual pages should adjust as they see fit.
 * Overall, it's an imperfect solution since on slow Internet connections
 * LaunchDarkly will time out with high probability.
 *
 * Bootstrapping from the server is the more ideal solution, but it's not
 * clear how it would work with the architecture of this Gatsby website.
 *
 * At the moment, bootstrapping uses localStorage, so a user who has loaded the
 * website already or accesses a page after accessing the main site will have
 * flags instantly available.
 */
export function useFlag(flag: string, options: UseFlagOptions = {defaultValue: false, timeout: 800}) {
  const flags = useFlags();
  const isReady = useLaunchDarkly();
  const [didTimeOut, setDidTimeOut] = useState(false);
  const [setHookTimeout, clearHookTimeout] = useTimeout();

  useEffect(() => {
    // If the client is already initialized, don't need to start a timeout.
    if (isReady) {
      clearHookTimeout();

      return;
    }

    // Otherwise, wait on initial render for the client to get an initial set of
    // flags.
    setHookTimeout(() => {
      // If the client already finished initializing in the timeout limit, we
      // don't need to do anything.
      if (isReady)
        return;

      // Otherwise, we timed out waiting for LaunchDarkly.
      setDidTimeOut(true);
    }, options.timeout);
  }, [isReady]);

  const flagValue = flags[flag];

  // If we are still loading or we timed out, return the default value.
  if (!isReady || didTimeOut)
    return [options.defaultValue, isReady];

  const returnedFlagValue = flagValue === undefined ? options.defaultValue : flagValue;

  return [returnedFlagValue, isReady];
}
